"use client";

import { styled } from "@mui/material/styles";
import Button, { ButtonProps } from "@mui/material/Button";
import Box from "@mui/material/Box";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft } from "@fortawesome/pro-solid-svg-icons/faArrowLeft";
import { faChevronLeft } from "@fortawesome/pro-solid-svg-icons/faChevronLeft";

interface FunnelBackButtonProps extends Omit<ButtonProps, "variant" | "color"> {
  showArrow?: boolean;
  iconOnly?: boolean;
  appearance?: "button" | "text";
}

/**
 * Back button for form funnel navigation.
 * Outlined variant that complements the primary FunnelButton.
 * When iconOnly=true (default), renders as a square button with just the arrow icon.
 */
export const FunnelBackButton = ({
  children,
  showArrow = true,
  iconOnly = true,
  appearance = "button",
  "aria-label": ariaLabel,
  ...props
}: FunnelBackButtonProps) => {
  const isTextLink = appearance === "text";
  const hasVisibleLabel = isTextLink || !iconOnly;

  return (
    <StyledBackButton
      data-navigation-action="back"
      color="secondary"
      $iconOnly={isTextLink ? false : iconOnly}
      $appearance={appearance}
      aria-label={ariaLabel ?? (hasVisibleLabel ? undefined : "Back")}
      {...props}
    >
      <ButtonContent>
        {showArrow && (
          <FontAwesomeIcon
            icon={isTextLink ? faChevronLeft : faArrowLeft}
            style={{ fontSize: isTextLink ? 12 : 14 }}
          />
        )}
        {(isTextLink || !iconOnly) && <span>{children || "Back"}</span>}
      </ButtonContent>
    </StyledBackButton>
  );
};

const StyledBackButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== "$iconOnly" && prop !== "$appearance",
})<{ $iconOnly?: boolean; $appearance: "button" | "text" }>`
  background-color: ${({ theme, $appearance }) =>
    $appearance === "text" ? "transparent" : theme.palette.dream.grayLight};
  border-radius: ${({ $appearance }) => ($appearance === "text" ? 0 : "8px")};
  padding: ${({ $iconOnly, $appearance }) =>
    $appearance === "text" ? "8px 0" : $iconOnly ? "16px" : "16px 18px"};
  font-weight: 600;
  font-size: ${({ $appearance }) => ($appearance === "text" ? "0.95rem" : "16px")};
  text-transform: none;
  min-height: ${({ $appearance }) => ($appearance === "text" ? "auto" : "48px")};
  min-width: ${({ $iconOnly }) => ($iconOnly ? "auto" : "auto")};
  width: ${({ $iconOnly, $appearance }) => ($appearance === "text" || $iconOnly ? "auto" : "100%")};
  max-width: ${({ $iconOnly, $appearance }) =>
    $appearance === "text" || $iconOnly ? "none" : "320px"};
  aspect-ratio: ${({ $iconOnly }) => ($iconOnly ? "1 / 1" : "auto")};
  display: flex;
  box-shadow: none;
  ${({ theme, $appearance }) =>
    $appearance === "text" ? `color: ${theme.palette.primary.main};` : ""}

  &:hover {
    color: ${({ theme }) => theme.palette.primary.main};
    background-color: ${({ $appearance }) =>
      $appearance === "text" ? "transparent" : "rgba(34, 34, 85, 0.04)"};
    border-color: ${({ theme }) => theme.palette.secondary.dark};
    box-shadow: none;
    text-decoration: ${({ $appearance }) => ($appearance === "text" ? "underline" : "none")};
  }

  &:active {
    color: ${({ theme }) => theme.palette.primary.main};
    background-color: ${({ $appearance }) =>
      $appearance === "text" ? "transparent" : "rgba(34, 34, 85, 0.08)"};
    transform: translateY(1px);
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.palette.secondary.main};
    outline-offset: 2px;
  }

  &:disabled {
    border-color: #b0b0b0;
    color: #b0b0b0;
    opacity: 0.7;
    background-color: ${({ theme, $appearance }) =>
      $appearance === "text" ? "transparent" : theme.palette.dream.grayLight};
  }
`;

const ButtonContent = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
`;
